import { useContext, useState } from 'react';

import { useQuery } from '@apollo/client';

import { FETCH_USER_CREATED_FAVORITE_RECIPES } from '../app/graphql/queries';
import { FavoritesRecipeSelector, RecipeListSelector } from '../app/homepage/homepage.slice';
import { SearchFilteredRecipesInterface } from '../app/search/search.types';
import { useAppSelector } from '../app/store/hooks';
import { ErrorManagementContext } from '../utils/errorManagement/ErrorManagementProvider';
import { recipeMetaDataTransformerWithoutIngredientsFS2QL } from '../utils/RecipeBackEnd/hasuraFSMigrationTransformers';

const getMissingIDs = (recipeList: SearchFilteredRecipesInterface[], favoriteRecipeIds: string[]) => {
  const favoriteRecipeSet = new Set(favoriteRecipeIds);
  const foundRecipes: SearchFilteredRecipesInterface[] = [];

  recipeList?.forEach(recipe => {
    if (favoriteRecipeSet.has(recipe.id)) {
      foundRecipes.push(recipe);
      favoriteRecipeSet.delete(recipe.id);
    }  
  }); 

  return {
    foundRecipes,
    missingIDs: Array.from(favoriteRecipeSet)
  };
};

const sortedRecipes = (recipes: SearchFilteredRecipesInterface[], favoriteRecipeIds: string[]) => {
  const orderedRecipes = recipes.sort(
    (a: SearchFilteredRecipesInterface, b: SearchFilteredRecipesInterface) => favoriteRecipeIds.indexOf(a.id) - favoriteRecipeIds.indexOf(b.id)
  );

  return orderedRecipes;
}

const useFavoriteRecipes = () => {
  const { cookClubLog } = useContext(ErrorManagementContext);
  
  const recipeList = useAppSelector(RecipeListSelector);
  const favoriteRecipeIds = useAppSelector(FavoritesRecipeSelector);
  
  const {foundRecipes, missingIDs} = getMissingIDs(recipeList, favoriteRecipeIds)
  const [recipes, setRecipes] = useState<SearchFilteredRecipesInterface[]>(foundRecipes);
  
  useQuery(FETCH_USER_CREATED_FAVORITE_RECIPES, {
    skip: missingIDs.length === 0,
    variables: { missingIDs },
    onCompleted: (data) => {
      const sanitizedData = recipeMetaDataTransformerWithoutIngredientsFS2QL(data?.Recipes_E3);
      const allRecipes = foundRecipes.concat(sanitizedData);
      setRecipes(allRecipes);
    },
    onError: (err) => {
      cookClubLog(
        "",
        "Unable to fetch favorite Recipes!",
        '',
        { ui: { type: "error" }, browserConsole: true })
      console.error('Error fetching favorite recipes:', err);
    }
  });

  return { recipes: sortedRecipes(recipes, favoriteRecipeIds) };
};

export default useFavoriteRecipes;

